import React from 'react';
import { Box, Flex, Text, Icon, useColorModeValue } from '@chakra-ui/react';
import { FiRadio, FiTruck, FiUsers, FiPackage, FiNavigation, FiPhone, FiCheckCircle } from 'react-icons/fi';

/**
 * Voice Simulation Flow Chart
 * Displays current phase and progress through simulation
 */

const PHASES = [
  { id: 'DISPATCH', label: 'Dispatch', icon: FiRadio, description: 'Receiving call' },
  { id: 'EN_ROUTE', label: 'En Route', icon: FiTruck, description: 'Responding to scene' },
  { id: 'ON_SCENE', label: 'On Scene', icon: FiUsers, description: 'Patient contact' },
  { id: 'LOADED', label: 'Loaded', icon: FiPackage, description: 'Patient in ambulance' },
  { id: 'TRANSPORTING', label: 'Transporting', icon: FiNavigation, description: 'En route to hospital' },
  { id: 'RADIO_REPORT', label: 'Radio Report', icon: FiPhone, description: 'Calling hospital' },
  { id: 'END', label: 'Complete', icon: FiCheckCircle, description: 'Simulation ended' }
];

export default function VoiceSimulationFlowChart({ currentPhase, compact = false }) {
  const activeBg = useColorModeValue('blue.500', 'blue.300');
  const activeColor = useColorModeValue('white', 'gray.900');
  const completedBg = useColorModeValue('green.100', 'green.900');
  const completedBorder = useColorModeValue('green.500', 'green.300');
  const upcomingBg = useColorModeValue('gray.100', 'gray.700');
  const upcomingBorder = useColorModeValue('gray.300', 'gray.600');
  const connectorColor = useColorModeValue('gray.300', 'gray.600');

  // Find current phase index
  const currentIndex = PHASES.findIndex(p => p.id === currentPhase);

  if (compact) {
    // Compact view: just show current phase name
    const phase = PHASES[currentIndex] || PHASES[0];
    return (
      <Flex align="center" gap={2}>
        <Icon as={phase.icon} boxSize={4} />
        <Text fontSize="sm" fontWeight="medium">{phase.label}</Text>
      </Flex>
    );
  }

  return (
    <Box>
      <Text fontSize="sm" fontWeight="semibold" mb={3} color="gray.600">
        Simulation Progress
      </Text>
      <Flex direction="row" align="center" justify="space-between" flexWrap="wrap" gap={2}>
        {PHASES.map((phase, index) => {
          const isActive = index === currentIndex;
          const isCompleted = index < currentIndex;
          const isUpcoming = index > currentIndex;

          return (
            <React.Fragment key={phase.id}>
              {/* Phase node */}
              <Flex
                direction="column"
                align="center"
                flex="1"
                minW="80px"
                position="relative"
              >
                {/* Icon circle */}
                <Flex
                  align="center"
                  justify="center"
                  w="48px"
                  h="48px"
                  borderRadius="full"
                  bg={isActive ? activeBg : isCompleted ? completedBg : upcomingBg}
                  borderWidth={2}
                  borderColor={isActive ? activeBg : isCompleted ? completedBorder : upcomingBorder}
                  color={isActive ? activeColor : isCompleted ? completedBorder : 'gray.500'}
                  transition="all 0.2s"
                  boxShadow={isActive ? 'lg' : 'sm'}
                >
                  <Icon as={phase.icon} boxSize={6} />
                </Flex>

                {/* Label */}
                <Text
                  mt={2}
                  fontSize="xs"
                  fontWeight={isActive ? 'bold' : 'medium'}
                  color={isActive ? activeBg : isCompleted ? completedBorder : 'gray.500'}
                  textAlign="center"
                >
                  {phase.label}
                </Text>

                {/* Description (only for active) */}
                {isActive && (
                  <Text
                    mt={1}
                    fontSize="2xs"
                    color="gray.500"
                    textAlign="center"
                  >
                    {phase.description}
                  </Text>
                )}
              </Flex>

              {/* Connector line (except after last phase) */}
              {index < PHASES.length - 1 && (
                <Box
                  flex="0 0 auto"
                  w="20px"
                  h="2px"
                  bg={index < currentIndex ? completedBorder : connectorColor}
                  transition="all 0.2s"
                />
              )}
            </React.Fragment>
          );
        })}
      </Flex>
    </Box>
  );
}
